import {useEffect, useRef, useState} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {Block} from '@/_new-code/services/kontent-ai/types'
import {RichTextRenderer} from '@/_new-code/products/flexible-web-toolkit/components/rich-text-renderer'

export type CardModalTriggerContentItem = IContentItem<{
	modalTriggerText: Elements.TextElement
	modalTitle: Elements.TextElement
	modalText: Elements.RichTextElement
	modalConfirmButton: Elements.TextElement
	modalConfirmHref: Elements.TextElement
	modalCancelButton: Elements.TextElement
	preConfirmationUsed: Elements.MultipleChoiceElement
	preConfirmationLabel: Elements.TextElement
	preConfirmationDescription: Elements.RichTextElement
}>

export const CardModalTriggerBlock: Block<CardModalTriggerContentItem> = ({
	block,
	...rest
}) => {
	const dialogRef = useRef<HTMLDialogElement>(null)
	const [consentChecked, setConsentChecked] = useState<boolean>(false)

	const isPreConfirmationRequired =
		block.elements.preConfirmationUsed[0]?.codename === 'yes'
	const isButtonDisabled = isPreConfirmationRequired && !consentChecked

	const openModal = (): void => {
		dialogRef.current?.showModal()
	}

	const closeModal = (): void => {
		dialogRef.current?.close()
	}

	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return

		const handleBackdropClick = (event: MouseEvent): void => {
			if (event.target === dialog) {
				dialog.close()
			}
		}

		dialog.addEventListener('click', handleBackdropClick)
		return (): void => {
			dialog.removeEventListener('click', handleBackdropClick)
		}
	}, [])

	return (
		<>
			<style>
				{`
                    .card-modal-rich-text p {
                        margin-bottom: 0;
                    }
                    .card-modal-rich-text li {
                        margin-bottom: 0;
                    }
                `}
			</style>
			<button
				className="h-10 w-full rounded-md border border-elanco-blue bg-elanco-blue px-4 py-2 text-center text-sm font-medium text-[#F8FAFC]"
				onClick={openModal}
				type="button"
			>
				{block.elements.modalTriggerText}
			</button>

			<dialog
				className="w-[calc(100%-2rem)] max-w-[32rem] rounded-lg border text-left"
				ref={dialogRef}
			>
				<div className="flex flex-col gap-4 p-6">
					<div className="flex items-center gap-2">
						<svg
							className="-mt-px"
							fill="none"
							height="24"
							viewBox="0 0 24 24"
							width="24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clipPath="url(#clip0_226_6465)">
								<path
									d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
									stroke="#0F172A"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
								/>
							</g>
							<defs>
								<clipPath id="clip0_226_6465">
									<rect fill="white" height="24" width="24" />
								</clipPath>
							</defs>
						</svg>

						<div>
							<h2 className="text-lg font-semibold leading-7 text-[#0F172A]">
								{block.elements.modalTitle}
							</h2>
						</div>
					</div>

					{/* Rich text confirmation content */}
					<RichTextRenderer
						className="card-modal-rich-text text-[#475569]"
						element={block.elements.modalText}
						{...rest}
					/>

					{/* Pre-confirmation checkbox */}
					{block.elements.preConfirmationUsed[0]?.codename ===
						'yes' && (
						<div className="flex items-start gap-2">
							<label className="mt-1 inline-flex cursor-pointer items-center">
								<input
									checked={consentChecked}
									className="peer sr-only"
									id="privacy-consent-checkbox"
									onChange={(e) => {
										setConsentChecked(e.target.checked)
									}}
									type="checkbox"
								/>

								<span className="relative flex h-4 w-4 items-center justify-center rounded-[2px] border border-[#005AA4] bg-white peer-checked:bg-[#005AA4] peer-focus:ring-2 peer-focus:ring-[#005AA4]/40 [&_svg]:opacity-0 peer-checked:[&_svg]:opacity-100">
									<svg
										className="h-3.5 w-3.5"
										fill="none"
										viewBox="0 0 16 16"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M13.3334 4L6.00002 11.3333L2.66669 8"
											stroke="#F8FAFC"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="1.33"
										/>
									</svg>
								</span>
							</label>
							<div className="flex flex-col gap-1.5">
								<label
									className="cursor-pointer text-base font-medium text-[#0F172A]"
									htmlFor="privacy-consent-checkbox"
								>
									{block.elements.preConfirmationLabel}
								</label>
								<RichTextRenderer
									className="text-sm text-[#475569]"
									element={
										block.elements
											.preConfirmationDescription
									}
									{...rest}
								/>
							</div>
						</div>
					)}

					<div className="flex gap-2 self-end">
						<button
							className="h-10 rounded-md border border-elanco-blue bg-white px-4 py-2 text-center text-sm font-medium text-elanco-blue"
							onClick={closeModal}
							type="button"
						>
							{block.elements.modalCancelButton}
						</button>

						{/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- Anchor is used for external navigation but conditionally disabled */}
						<a
							aria-disabled={isButtonDisabled}
							className={`h-10 rounded-md border border-elanco-blue bg-elanco-blue px-4 py-2 text-center text-sm font-medium text-[#F8FAFC] ${
								isButtonDisabled
									? 'pointer-events-none opacity-50'
									: ''
							}`}
							href={
								isButtonDisabled
									? '#'
									: block.elements.modalConfirmHref
							}
							onClick={
								isButtonDisabled
									? (e): void => {
											e.preventDefault()
										}
									: undefined
							}
							rel="noopener noreferrer"
							target="_blank"
						>
							{block.elements.modalConfirmButton}
						</a>
					</div>
				</div>
			</dialog>
		</>
	)
}
